import { useEffect, useMemo, useRef, useState } from "react";
import { searchPois, type Poi } from "@/data/pois.ts";
import { formatCoord, type GameCoord } from "@/engine/coords.ts";
import { fromGridRef, gridLabel, type GridConfig } from "@/engine/grid.ts";
import { parseCoord } from "@/engine/coords.ts";

interface PlaceSearchProps {
  readonly mapId: string;
  readonly grid: GridConfig;
  readonly onGo: (at: GameCoord, label: string) => void;
}

type Result =
  | { readonly kind: "poi"; readonly poi: Poi; readonly at: GameCoord }
  | { readonly kind: "grid"; readonly label: string; readonly at: GameCoord }
  | { readonly kind: "coord"; readonly at: GameCoord };

/**
 * Find a place.
 *
 * One box that accepts all three ways people refer to a location, because
 * under pressure nobody stops to pick the right field:
 *
 *   "tower"          a name, fuzzily matched
 *   "D5-7"           a callout reference
 *   "x105.5 y109.1"  a pasted coordinate
 *
 * Opens on `/` — the same key every chat box in every game uses — and closes
 * on Escape. Arrow keys and Enter, so a hand never has to leave the keyboard.
 */
export function PlaceSearch({ mapId, grid, onGo }: PlaceSearchProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target instanceof HTMLElement) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) return;
      }
      event.preventDefault();
      setOpen(true);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else {
      setQuery("");
      setHighlighted(0);
    }
  }, [open]);

  const results = useMemo<Result[]>(() => {
    const trimmed = query.trim();
    if (trimmed.length === 0) return [];

    const found: Result[] = [];

    // A grid reference is unambiguous, so it goes first when it parses.
    const gridAt = fromGridRef(trimmed, grid);
    if (gridAt) found.push({ kind: "grid", label: trimmed.toUpperCase(), at: gridAt });

    // Only treat input as a coordinate when it actually looks like a pair;
    // otherwise "Tower 4" would be read as the number 4.
    if (/\d[\s,]+\d/.test(trimmed) || /[xy]\s*\d/i.test(trimmed)) {
      const coord = parseCoord(trimmed);
      if (coord) found.push({ kind: "coord", at: coord });
    }

    for (const poi of searchPois(trimmed, mapId)) {
      found.push({ kind: "poi", poi, at: poi.at });
    }

    return found.slice(0, 8);
  }, [query, grid, mapId]);

  const go = (result: Result | undefined): void => {
    if (!result) return;
    const label =
      result.kind === "poi"
        ? result.poi.label
        : result.kind === "grid"
          ? result.label
          : gridLabel(result.at, grid, 1);
    onGo(result.at, label);
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Find a place (/)"
        className="flex items-center gap-1.5 rounded border border-border-subtle px-2 py-1 text-xs text-text-muted transition-colors hover:border-border-strong hover:text-text-secondary"
      >
        Find
        <span className="text-[10px]">/</span>
      </button>
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setHighlighted(0);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setOpen(false);
            return;
          }
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setHighlighted((index) => Math.min(results.length - 1, index + 1));
            return;
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlighted((index) => Math.max(0, index - 1));
            return;
          }
          if (event.key === "Enter") {
            event.preventDefault();
            go(results[highlighted]);
          }
        }}
        placeholder="Name, grid, or coordinate"
        aria-label="Find a place"
        className="w-56 rounded border border-accent bg-surface-sunken px-2 py-1 text-xs text-text-primary placeholder:text-text-muted focus:outline-none"
      />

      {results.length > 0 && (
        <ul className="absolute top-full left-0 z-20 mt-1 w-72 overflow-hidden rounded border border-border-strong bg-surface-raised shadow-lg">
          {results.map((result, index) => (
            <li key={`${result.kind}-${index}`}>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => go(result)}
                onMouseEnter={() => setHighlighted(index)}
                className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left"
                style={{
                  backgroundColor:
                    index === highlighted ? "var(--color-surface-sunken)" : "transparent",
                }}
              >
                <span className="truncate text-xs text-text-primary">
                  {result.kind === "poi"
                    ? result.poi.label
                    : result.kind === "grid"
                      ? `Grid ${result.label}`
                      : "Coordinate"}
                </span>
                <span className="numeric shrink-0 text-[10px] text-text-muted">
                  {result.kind === "poi"
                    ? gridLabel(result.at, grid, 1)
                    : formatCoord(result.at, 1)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
